import { Suspense, type ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { Image } from '@/routes/-components/image';
import { diamondGlows, DiamondGrid } from '../primitives';
import { PartnerLogo } from '../partner-logo';
import { Expand } from '../expand';
import { MailboxIllustration } from './mailbox-illustration';
import { CallIcon, LocationIcon } from './icons';
import type { Audience } from './audiences';

/** The office, as the hand-off writes it. `www/contact` still says 108 — 112 is the number to keep. */
const ADDRESS_LINES = ['Carrer de Llull, 112, Sant Martí, 08005', 'Barcelona, España.'];
const MAPS_URL = 'https://maps.google.com/?q=Carrer+de+Llull+112+08005+Barcelona';
const PHONE_DISPLAY = '(+34) 936 11 01 04';
const PHONE_HREF = 'tel:+34936110104';

/** Measured off this page's own export, not rounded to the homepage's 5/27. */
const DIAMOND_TILE = { size: 4.39, gap: 27.59 } as const;

/** As many as fit the designed width without the logos shrinking to nothing. */
const LOGO_COUNT = 6;

/**
 * Communications' choice and order, not a ranking. By id because the names are not distinctive: the
 * table also holds a "CREU ROJA", a "Cruz Roja Chilena" and a "UNICEF Andorra".
 */
export const CONTACT_NGO_IDS = [
	'8d9d61a5-cff7-4c4c-a439-f2632cd33de7', // Cruz Roja
	'cd54c853-c0a2-4726-91d8-a4fb3e23cc81', // Fundación Ana Bella
	'73fbaead-f1c2-4444-9d44-2f5ff8b39c4e', // DOWN España
	'c051d936-35e7-497b-bb21-44e44570bcd9', // Hospital Sant Joan de Déu
	'e16293db-50b7-4241-92c0-3b52d050b807', // UNICEF
] as const;

/** Unboxed, a deliberate departure from the hand-off's linen card. */
function LogoStrip(props: { label: string; logos: Array<{ id: string; name?: string; location?: string }> }) {
	if (props.logos.length === 0) return null;
	return (
		<ul aria-label={props.label} className="flex h-[104px] items-center justify-between gap-6 md:gap-10">
			{props.logos.map((logo) => (
				<li key={logo.id} className="flex h-16 min-w-0 flex-1 items-center justify-center">
					<PartnerLogo src={logo.location} alt={logo.name ?? logo.id} size="lg" />
				</li>
			))}
		</ul>
	);
}

/** From the homepage's stats: a measured list cannot name a merchant we no longer work with. */
function MerchantLogos() {
	const { t } = useLingui();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const logos = stats.partnerMerchants
		.filter((merchant) => merchant.logo)
		.slice(0, LOGO_COUNT)
		.map((merchant) => ({ id: merchant.id, name: merchant.name, location: merchant.logo?.location }));
	return <LogoStrip label={t`Partner company logos`} logos={logos} />;
}

/**
 * Hand-picked, as a BI ranking is no argument and degrades to alphabetical ("A. P. S. Piccoli Battiti"
 * first). An id that stops resolving drops out instead of breaking the row.
 */
function NgoLogos() {
	const { t } = useLingui();
	const { data } = useSuspenseQuery(trpc.portal.getNgoLogos.queryOptions({ ngoIds: [...CONTACT_NGO_IDS] }));
	const logos = data.ngos.map((ngo) => ({ id: ngo.id, name: ngo.name, location: ngo.logo.location }));
	return <LogoStrip label={t`Partner NGO logos`} logos={logos} />;
}

/** A company sees the brands, everybody else the NGOs; the Suspense wraps the strip alone. */
function PartnerLogos({ audience }: { audience: Audience | null }) {
	return audience === 'company' ? <MerchantLogos /> : <NgoLogos />;
}

function ContactDetail({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
	return (
		<div className="relative flex flex-col gap-2">
			<div className="text-ink flex flex-col gap-1 opacity-50">
				{icon}
				<span className="text-sm font-medium tracking-[-0.02em]">{label}</span>
			</div>
			{children}
		</div>
	);
}

/**
 * The hand-off runs the post off the bottom edge, so the card's clipping is load-bearing. Hidden below
 * `lg`, where the address wraps past its 276px and the drawing would cover it.
 */
const ILLUSTRATION_BOX =
	'pointer-events-none absolute top-[26px] left-[51%] hidden aspect-[370/358] w-[46.6%] lg:block';

function Whereabouts() {
	const { t } = useLingui();
	return (
		<Expand index={0.5} asChild>
			<div className="bg-sage relative flex flex-col gap-8 overflow-hidden rounded-3xl p-10 lg:h-[308px]">
				{/* The hand-off's other three ellipses here fall almost entirely outside the card. */}
				<DiamondGrid color="#35433F" opacity={1} {...DIAMOND_TILE} masks={[diamondGlows({ x: '50.5%', y: '131%' })]} />

				<MailboxIllustration label={t`An envelope going into a letterbox`} className={ILLUSTRATION_BOX} />

				<ContactDetail icon={<LocationIcon className="size-4" />} label={t`Want to visit us?`}>
					<a
						href={MAPS_URL}
						target="_blank"
						rel="noopener noreferrer"
						className="hover:text-brand-strong max-w-[276px] leading-[1.5] transition-colors"
					>
						{ADDRESS_LINES.map((line) => (
							<span key={line} className="block">
								{line}
							</span>
						))}
					</a>
				</ContactDetail>

				<ContactDetail icon={<CallIcon className="size-4" />} label={t`You can also call us`}>
					<a href={PHONE_HREF} className="hover:text-brand-strong leading-[1.5] transition-colors">
						{PHONE_DISPLAY}
					</a>
				</ContactDetail>
			</div>
		</Expand>
	);
}

/** Does NOT stretch: the photograph would grow past 900px as a visitor ticks through the questionnaire. */
export function ContactInfo({ audience }: { audience: Audience | null }) {
	const { t } = useLingui();
	return (
		<div className="flex flex-col gap-4">
			<Expand index={0} asChild>
				<div className="relative aspect-[793/357] overflow-hidden rounded-3xl">
					<Image
						src="/redesign/contact/hero.jpg"
						alt={t`Children smiling outside their school`}
						height={682}
						aspectRatio={1024 / 682}
						objectFit="cover"
						priority
						className="absolute inset-0 size-full object-cover!"
					/>
				</div>
			</Expand>
			<Suspense fallback={<div className="h-[104px]" />}>
				<PartnerLogos audience={audience} />
			</Suspense>
			<Whereabouts />
		</div>
	);
}
